/* global PiniaSetupStoreHelpers */
import type { UnwrapRef } from 'vue';
import type { Writable } from 'type-fest';
import type { ResultItem } from '@/utils/file';
import type { RequiredKeys } from '@/utils/types';
import type {
  InitialData as TicketsDashboardSearchFormInitialData,
  ValidData as TicketsDashboardSearchFormValidData,
} from '@/components/searchForms/TicketsDashboardSearchForm.vue';
import type { TicketsDashboardViewDataComposable } from '@/composables/data/useTicketsDashboardViewData';
import type {
  DefRouteQuery as TicketsDefRouteQuery,
  RouteQueryComposable as TicketsRouteQueryComposable,
} from '@/composables/routeQueries/useTicketsRouteQuery';

import { computed, reactive, toRefs, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { defineStore } from 'pinia';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';

import useDownloadCsv from '@/composables/useDownloadCsv';
import * as locationQueryHelpers from '@/helpers/locationQuery';
import * as ticketsDashboardTableHelpers from '@/helpers/tables/ticketsDashboard';
import * as routeNames from '@/constants/routeNames';

interface DefPrivateTicketsDashboardViewState {
  data: RequiredKeys<
    Partial<Writable<UnwrapRef<TicketsDashboardViewDataComposable>>>
  >;
  query: RequiredKeys<TicketsDefRouteQuery>;
}

interface DefTicketsDashboardViewState {
  selectedResultId: number;
}

const defPrivateTicketsDashboardViewState: DefPrivateTicketsDashboardViewState =
  {
    data: {
      isFetchingJobTicketsData: undefined,
      jobTicketsData: undefined,
      jobTicketsDataError: undefined,
      refreshJobTicketsData: undefined,
      reload: undefined,
    },
    query: {
      curPage: undefined,
      pageSize: undefined,
      status: undefined,
      search: undefined,
      isRfc: undefined,
      isUnread: undefined,
    },
  };

const defTicketsDashboardViewState: DefTicketsDashboardViewState = {
  selectedResultId: NaN,
};

function privateTicketsDashboardViewStoreSetup(
  helpers: PiniaSetupStoreHelpers,
) {
  const state = reactive(
    _cloneDeep(defPrivateTicketsDashboardViewState),
  ) as DefPrivateTicketsDashboardViewState;
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const route = useRoute() || {}; // TODO mock useRoute

  async function $reset(): Promise<void> {
    const reload = state.data.reload;
    const query = state.query;
    Object.assign(state, _cloneDeep(defPrivateTicketsDashboardViewState));
    if (route.name === routeNames.JOB_TICKETS) {
      state.query = query; // preserve query while on the page
      if (reload) {
        setData(reload()); // reload data while on the page
      }
    }
  }

  watch(
    () => route.name,
    function () {
      if (route.name !== routeNames.JOB_TICKETS) {
        void $reset(); // reset when leave the page
      }
    },
    { immediate: true },
  );

  function setData(viewData: TicketsDashboardViewDataComposable) {
    state.data = reactive(viewData);
  }

  function setQuery(routeQuery: TicketsRouteQueryComposable) {
    state.query = reactive(routeQuery);
  }

  return {
    ...toRefs(state),
    $reset,
    setData,
    setQuery,
  };
}

function ticketsDashboardViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defTicketsDashboardViewState));
  const route = useRoute();
  const router = useRouter();

  const _ticketsDashboardViewStore = usePrivateTicketsDashboardViewStore();

  const data = computed(function () {
    return _ticketsDashboardViewStore.data;
  });

  const query = computed(function () {
    return _ticketsDashboardViewStore.query;
  });

  const ticketsDashboardTableData = computed(function () {
    if (data.value.jobTicketsDataError) return [];
    return ticketsDashboardTableHelpers.getTableData(data.value.jobTicketsData);
  });

  const initialData = computed<TicketsDashboardSearchFormInitialData>(() => {
    return {
      status: query.value.status,
      search: query.value.search,
      isRfc: query.value.isRfc,
      isUnread: query.value.isUnread,
    };
  });

  const { isDownloadingCSV, downloadCsv } = useDownloadCsv();

  const resultsForCsv = computed<ResultItem[]>(() => {
    const results = data.value.jobTicketsData?.results;
    if (!Array.isArray(results)) return [];
    return results.map((item) => {
      const { jobs, ...resultItem } = item;
      return {
        ...resultItem,
        jobs: jobs?.join(','),
      };
    });
  });

  async function $reset(): Promise<void> {
    Object.assign(state, _cloneDeep(defTicketsDashboardViewState));
    _ticketsDashboardViewStore.$reset();
  }

  function init(
    viewData: TicketsDashboardViewDataComposable,
    routeQuery: TicketsRouteQueryComposable,
  ) {
    _ticketsDashboardViewStore.setData(viewData);
    _ticketsDashboardViewStore.setQuery(routeQuery);
  }

  function search(formData: TicketsDashboardSearchFormValidData): void {
    const query = locationQueryHelpers.buildTicketsDashboardSearchLocationQuery(
      route.query,
      formData,
    );
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.JOB_TICKETS,
      query,
      force: true,
      replace: isReplace,
    });
  }

  function downloadCSV() {
    downloadCsv(resultsForCsv.value, 'ticketsDashboard');
  }

  return {
    ...toRefs(state),
    data,
    query,
    ticketsDashboardTableData,
    initialData,
    isDownloadingCSV,
    $reset,
    init,
    search,
    downloadCSV,
  };
}

const usePrivateTicketsDashboardViewStore = defineStore(
  '_ticketsDashboardView',
  privateTicketsDashboardViewStoreSetup,
);

export const useTicketsDashboardViewStore = defineStore(
  'ticketsDashboardView',
  ticketsDashboardViewStoreSetup,
);
